import type { ComponentType } from "react";

type Loader = () => Promise<{ default: ComponentType }>;

// slug → dynamic import of that article's chunk. Using import() (not static
// imports) keeps the ~20 article bundles — and the shared GuideLayout — OUT of
// the initial app bundle that every page, including the homepage, downloads;
// each guide is fetched only when its /blog/<slug> route renders. data/guides.ts
// is the metadata source of truth; keep it and scripts/routes.mjs in sync when
// adding a post. Everything is served under /blog/<slug>.
const loaders: Record<string, Loader> = {
  "real-estate-social-media-marketing": () =>
    import("./guides/RealEstateSocialMediaMarketingGuide").then((m) => ({
      default: m.RealEstateSocialMediaMarketingGuide,
    })),
  "buyer-consultation-guide": () =>
    import("./guides/BuyerConsultationGuide").then((m) => ({
      default: m.BuyerConsultationGuide,
    })),
  "mortgage-term-explained": () =>
    import("./guides/MortgageTermGuide").then((m) => ({
      default: m.MortgageTermGuide,
    })),
  "gifted-deposit-mortgage-explained": () =>
    import("./guides/GiftedDepositGuide").then((m) => ({
      default: m.GiftedDepositGuide,
    })),
  "buy-to-let-mortgage-affordability": () =>
    import("./guides/BuyToLetAffordabilityGuide").then((m) => ({
      default: m.BuyToLetAffordabilityGuide,
    })),
  "how-lenders-treat-variable-income": () =>
    import("./guides/VariableIncomeMortgageGuide").then((m) => ({
      default: m.VariableIncomeMortgageGuide,
    })),
  "mortgage-in-principle-explained": () =>
    import("./guides/MortgageInPrincipleGuide").then((m) => ({
      default: m.MortgageInPrincipleGuide,
    })),
  "pre-approval-vs-pre-qualification": () =>
    import("./guides/PreApprovalVsPreQualificationGuide").then((m) => ({
      default: m.PreApprovalVsPreQualificationGuide,
    })),
  "cash-offer-vs-financed-offer": () =>
    import("./guides/CashOfferVsFinancedOfferGuide").then((m) => ({
      default: m.CashOfferVsFinancedOfferGuide,
    })),
  "counter-offer-strategy-buying-a-house": () =>
    import("./guides/CounterOfferStrategyGuide").then((m) => ({
      default: m.CounterOfferStrategyGuide,
    })),
  "how-to-write-an-offer-letter": () =>
    import("./guides/HowToWriteAnOfferLetterGuide").then((m) => ({
      default: m.HowToWriteAnOfferLetterGuide,
    })),
  "best-and-final-offer-strategy": () =>
    import("./guides/BestAndFinalOfferGuide").then((m) => ({
      default: m.BestAndFinalOfferGuide,
    })),
  "real-estate-contingencies-explained": () =>
    import("./guides/RealEstateContingenciesGuide").then((m) => ({
      default: m.RealEstateContingenciesGuide,
    })),
  "earnest-money-deposit-explained": () =>
    import("./guides/EarnestMoneyDepositGuide").then((m) => ({
      default: m.EarnestMoneyDepositGuide,
    })),
  "escalation-clause-explained": () =>
    import("./guides/EscalationClauseGuide").then((m) => ({
      default: m.EscalationClauseGuide,
    })),
  "real-estate-farming-strategy": () =>
    import("./guides/RealEstateFarmingStrategyGuide").then((m) => ({
      default: m.RealEstateFarmingStrategyGuide,
    })),
  "pricing-pyramid-real-estate": () =>
    import("./guides/PricingPyramidGuide").then((m) => ({
      default: m.PricingPyramidGuide,
    })),
  "how-to-handle-multiple-offers": () =>
    import("./guides/HandleMultipleOffersGuide").then((m) => ({
      default: m.HandleMultipleOffersGuide,
    })),
  "sellers-net-sheet-explained": () =>
    import("./guides/SellersNetSheetGuide").then((m) => ({
      default: m.SellersNetSheetGuide,
    })),
  "when-to-reduce-asking-price": () =>
    import("./guides/WhenToReduceAskingPriceGuide").then((m) => ({
      default: m.WhenToReduceAskingPriceGuide,
    })),
  "valuation-confidence-score-explained": () =>
    import("./guides/ValuationConfidenceScoreGuide").then((m) => ({
      default: m.ValuationConfidenceScoreGuide,
    })),
  "property-valuation-data-sources": () =>
    import("./guides/PropertyValuationDataSourcesGuide").then((m) => ({
      default: m.PropertyValuationDataSourcesGuide,
    })),
  "sale-to-list-price-ratio": () =>
    import("./guides/SaleToListPriceRatioGuide").then((m) => ({
      default: m.SaleToListPriceRatioGuide,
    })),
  "days-on-market-explained": () =>
    import("./guides/DaysOnMarketExplainedGuide").then((m) => ({
      default: m.DaysOnMarketExplainedGuide,
    })),
  "how-to-handle-a-low-appraisal": () =>
    import("./guides/HowToHandleALowAppraisalGuide").then((m) => ({
      default: m.HowToHandleALowAppraisalGuide,
    })),
  "appraisal-gap-clause-explained": () =>
    import("./guides/AppraisalGapClauseGuide").then((m) => ({
      default: m.AppraisalGapClauseGuide,
    })),
  "subject-to-sale-offer-explained": () =>
    import("./guides/SubjectToSaleOfferGuide").then((m) => ({
      default: m.SubjectToSaleOfferGuide,
    })),
  "how-to-negotiate-after-a-home-inspection": () =>
    import("./guides/NegotiateAfterHomeInspectionGuide").then((m) => ({
      default: m.NegotiateAfterHomeInspectionGuide,
    })),
  "price-per-square-metre-valuation": () =>
    import("./guides/PricePerSquareMetreValuationGuide").then((m) => ({
      default: m.PricePerSquareMetreValuationGuide,
    })),
  "property-valuation-report-template": () =>
    import("./guides/PropertyValuationReportTemplateGuide").then((m) => ({
      default: m.PropertyValuationReportTemplateGuide,
    })),
  "how-to-present-a-cma-to-a-seller": () =>
    import("./guides/HowToPresentACmaToASellerGuide").then((m) => ({
      default: m.HowToPresentACmaToASellerGuide,
    })),
  "win-the-listing-with-pricing": () =>
    import("./guides/WinTheListingWithPricingGuide").then((m) => ({
      default: m.WinTheListingWithPricingGuide,
    })),
  "cma-vs-appraisal-difference": () =>
    import("./guides/CmaVsAppraisalGuide").then((m) => ({
      default: m.CmaVsAppraisalGuide,
    })),
  "broker-price-opinion-vs-cma": () =>
    import("./guides/BrokerPriceOpinionVsCmaGuide").then((m) => ({
      default: m.BrokerPriceOpinionVsCmaGuide,
    })),
  "cma-report-what-to-include": () =>
    import("./guides/CmaReportWhatToIncludeGuide").then((m) => ({
      default: m.CmaReportWhatToIncludeGuide,
    })),
  "avm-vs-cma-which-to-use": () =>
    import("./guides/AvmVsCmaWhichToUseGuide").then((m) => ({
      default: m.AvmVsCmaWhichToUseGuide,
    })),
  "desktop-appraisal-vs-avm": () =>
    import("./guides/DesktopAppraisalVsAvmGuide").then((m) => ({
      default: m.DesktopAppraisalVsAvmGuide,
    })),
  "hedonic-pricing-model-explained": () =>
    import("./guides/HedonicPricingModelGuide").then((m) => ({
      default: m.HedonicPricingModelGuide,
    })),
  "how-accurate-are-online-home-value-estimates": () =>
    import("./guides/HowAccurateAreOnlineHomeValueEstimatesGuide").then((m) => ({
      default: m.HowAccurateAreOnlineHomeValueEstimatesGuide,
    })),
  "active-sold-expired-comps": () =>
    import("./guides/ActiveSoldExpiredCompsGuide").then((m) => ({
      default: m.ActiveSoldExpiredCompsGuide,
    })),
  "how-to-do-a-real-estate-market-analysis": () =>
    import("./guides/HowToDoMarketAnalysisGuide").then((m) => ({
      default: m.HowToDoMarketAnalysisGuide,
    })),
  "absorption-rate-real-estate": () =>
    import("./guides/AbsorptionRateGuide").then((m) => ({
      default: m.AbsorptionRateGuide,
    })),
  "real-estate-lead-generation-ideas": () =>
    import("./guides/RealEstateLeadGenerationGuide").then((m) => ({
      default: m.RealEstateLeadGenerationGuide,
    })),
  "open-house-lead-generation-ideas": () =>
    import("./guides/OpenHouseLeadGenerationGuide").then((m) => ({
      default: m.OpenHouseLeadGenerationGuide,
    })),
  "seller-lead-nurture-email-sequence": () =>
    import("./guides/SellerLeadNurtureEmailSequenceGuide").then((m) => ({
      default: m.SellerLeadNurtureEmailSequenceGuide,
    })),
  "real-estate-landing-page-best-practices": () =>
    import("./guides/RealEstateLandingPageGuide").then((m) => ({
      default: m.RealEstateLandingPageGuide,
    })),
  "best-real-estate-tools-for-agents": () =>
    import("./guides/BestRealEstateToolsGuide").then((m) => ({
      default: m.BestRealEstateToolsGuide,
    })),
  "real-estate-crm-for-agents": () =>
    import("./guides/RealEstateCrmForAgentsGuide").then((m) => ({
      default: m.RealEstateCrmForAgentsGuide,
    })),
  "speed-to-lead-real-estate": () =>
    import("./guides/SpeedToLeadGuide").then((m) => ({
      default: m.SpeedToLeadGuide,
    })),
  "real-estate-referrals-guide": () =>
    import("./guides/RealEstateReferralsGuide").then((m) => ({
      default: m.RealEstateReferralsGuide,
    })),
  "listing-presentation-guide": () =>
    import("./guides/ListingPresentationGuide").then((m) => ({
      default: m.ListingPresentationGuide,
    })),
  "handle-seller-objections-listing": () =>
    import("./guides/HandleSellerObjectionsGuide").then((m) => ({
      default: m.HandleSellerObjectionsGuide,
    })),
  "how-to-create-a-cma": () =>
    import("./guides/HowToCreateCmaGuide").then((m) => ({
      default: m.HowToCreateCmaGuide,
    })),
  "how-to-value-a-rental-property": () =>
    import("./guides/HowToValueARentalPropertyGuide").then((m) => ({
      default: m.HowToValueARentalPropertyGuide,
    })),
  "cma-price-adjustments-explained": () =>
    import("./guides/CmaPriceAdjustmentsGuide").then((m) => ({
      default: m.CmaPriceAdjustmentsGuide,
    })),
  "cma-software-for-real-estate-agents": () =>
    import("./guides/CmaSoftwareForAgentsGuide").then((m) => ({
      default: m.CmaSoftwareForAgentsGuide,
    })),
  "free-vs-paid-cma-tools": () =>
    import("./guides/FreeVsPaidCmaToolsGuide").then((m) => ({
      default: m.FreeVsPaidCmaToolsGuide,
    })),
  "white-label-property-report-software": () =>
    import("./guides/WhiteLabelPropertyReportSoftwareGuide").then((m) => ({
      default: m.WhiteLabelPropertyReportSoftwareGuide,
    })),
  "cloud-cma-alternative": () =>
    import("./guides/CloudCmaAlternativeGuide").then((m) => ({
      default: m.CloudCmaAlternativeGuide,
    })),
  "home-valuation-widget-for-your-website": () =>
    import("./guides/HomeValuationWidgetGuide").then((m) => ({
      default: m.HomeValuationWidgetGuide,
    })),
  "idx-website-for-real-estate-agents": () =>
    import("./guides/IdxWebsiteForAgentsGuide").then((m) => ({
      default: m.IdxWebsiteForAgentsGuide,
    })),
  "what-is-gazumping-and-how-to-avoid-it": () =>
    import("./guides/GazumpingGuide").then((m) => ({
      default: m.GazumpingGuide,
    })),
  "how-to-find-comparable-sales-comps": () =>
    import("./guides/HowToFindCompsGuide").then((m) => ({
      default: m.HowToFindCompsGuide,
    })),
  "what-is-an-avm-automated-valuation-model": () =>
    import("./guides/WhatIsAnAvmGuide").then((m) => ({
      default: m.WhatIsAnAvmGuide,
    })),
  "how-to-price-a-listing": () =>
    import("./guides/HowToPriceAListingGuide").then((m) => ({
      default: m.HowToPriceAListingGuide,
    })),
  "prelisting-package-guide": () =>
    import("./guides/PrelistingPackageGuide").then((m) => ({
      default: m.PrelistingPackageGuide,
    })),
  "how-to-value-a-condo-apartment": () =>
    import("./guides/HowToValueACondoApartmentGuide").then((m) => ({
      default: m.HowToValueACondoApartmentGuide,
    })),
  "how-to-value-a-house": () =>
    import("./guides/HowToValueAHouseGuide").then((m) => ({
      default: m.HowToValueAHouseGuide,
    })),
  "how-much-should-i-offer-on-a-house": () =>
    import("./guides/HowMuchToOfferGuide").then((m) => ({
      default: m.HowMuchToOfferGuide,
    })),
  "borrowing-capacity-explained": () =>
    import("./guides/BorrowingCapacityGuide").then((m) => ({
      default: m.BorrowingCapacityGuide,
    })),
  "negative-equity-explained": () =>
    import("./guides/NegativeEquityGuide").then((m) => ({
      default: m.NegativeEquityGuide,
    })),
  "how-interest-rates-affect-borrowing": () =>
    import("./guides/HowInterestRatesAffectBorrowingGuide").then((m) => ({
      default: m.HowInterestRatesAffectBorrowingGuide,
    })),
  "mortgage-stress-test-explained": () =>
    import("./guides/MortgageStressTestGuide").then((m) => ({
      default: m.MortgageStressTestGuide,
    })),
  "fixed-vs-variable-mortgage-rate": () =>
    import("./guides/FixedVsVariableMortgageRateGuide").then((m) => ({
      default: m.FixedVsVariableMortgageRateGuide,
    })),
  "self-employed-mortgage-guide": () =>
    import("./guides/SelfEmployedMortgageGuide").then((m) => ({
      default: m.SelfEmployedMortgageGuide,
    })),
  "debt-to-income-ratio-explained": () =>
    import("./guides/DebtToIncomeRatioGuide").then((m) => ({
      default: m.DebtToIncomeRatioGuide,
    })),
  "how-to-calculate-home-equity": () =>
    import("./guides/HowToCalculateHomeEquityGuide").then((m) => ({
      default: m.HowToCalculateHomeEquityGuide,
    })),
  "mortgage-advisor-client-report-tools": () =>
    import("./guides/MortgageAdvisorClientReportToolsGuide").then((m) => ({
      default: m.MortgageAdvisorClientReportToolsGuide,
    })),
  "how-much-deposit-do-you-need": () =>
    import("./guides/HowMuchDepositDoYouNeedGuide").then((m) => ({
      default: m.HowMuchDepositDoYouNeedGuide,
    })),
  "interest-only-vs-repayment-mortgage": () =>
    import("./guides/InterestOnlyVsRepaymentMortgageGuide").then((m) => ({
      default: m.InterestOnlyVsRepaymentMortgageGuide,
    })),
  "refinancing-a-mortgage-explained": () =>
    import("./guides/RefinancingAMortgageExplainedGuide").then((m) => ({
      default: m.RefinancingAMortgageExplainedGuide,
    })),
  "mortgage-affordability-explained": () =>
    import("./guides/MortgageAffordabilityGuide").then((m) => ({
      default: m.MortgageAffordabilityGuide,
    })),
  "guarantor-mortgage-explained": () =>
    import("./guides/GuarantorMortgageGuide").then((m) => ({
      default: m.GuarantorMortgageGuide,
    })),
  "loan-to-value-ltv-explained": () =>
    import("./guides/LoanToValueLtvGuide").then((m) => ({
      default: m.LoanToValueLtvGuide,
    })),
  "how-to-win-a-bidding-war": () =>
    import("./guides/HowToWinABiddingWarGuide").then((m) => ({
      default: m.HowToWinABiddingWarGuide,
    })),
  "how-to-tell-if-a-house-is-overpriced": () =>
    import("./guides/HowToTellIfAHouseIsOverpricedGuide").then((m) => ({
      default: m.HowToTellIfAHouseIsOverpricedGuide,
    })),
  "how-to-spot-an-underpriced-house": () =>
    import("./guides/HowToSpotAnUnderpricedHouseGuide").then((m) => ({
      default: m.HowToSpotAnUnderpricedHouseGuide,
    })),
  "documents-needed-to-buy-a-house": () =>
    import("./guides/DocumentsNeededToBuyAHouseGuide").then((m) => ({
      default: m.DocumentsNeededToBuyAHouseGuide,
    })),
  "proof-of-funds-explained": () =>
    import("./guides/ProofOfFundsGuide").then((m) => ({
      default: m.ProofOfFundsGuide,
    })),
  "how-to-read-a-purchase-contract": () =>
    import("./guides/HowToReadAPurchaseContractGuide").then((m) => ({
      default: m.HowToReadAPurchaseContractGuide,
    })),
  "home-appraisal-process-explained": () =>
    import("./guides/HomeAppraisalProcessGuide").then((m) => ({
      default: m.HomeAppraisalProcessGuide,
    })),
  "seller-disclosure-statement-explained": () =>
    import("./guides/SellerDisclosureStatementGuide").then((m) => ({
      default: m.SellerDisclosureStatementGuide,
    })),
  "onder-de-vraagprijs-bieden": () =>
    import("./guides/OnderVraagprijsBiedenGuide").then((m) => ({
      default: m.OnderVraagprijsBiedenGuide,
    })),
  "voorbehoud-financiering": () =>
    import("./guides/VoorbehoudFinancieringGuide").then((m) => ({
      default: m.VoorbehoudFinancieringGuide,
    })),
  "bedenktijd-huis-kopen": () =>
    import("./guides/BedenktijdGuide").then((m) => ({ default: m.BedenktijdGuide })),
  "biedstrategie-huis-kopen": () =>
    import("./guides/BiedstrategieGuide").then((m) => ({ default: m.BiedstrategieGuide })),
  "hoeveel-overbieden-nederland-2026": () =>
    import("./guides/HoeveedOverbiedenGuide").then((m) => ({ default: m.HoeveedOverbiedenGuide })),
  "verschil-taxatiewaarde-en-vraagprijs": () =>
    import("./guides/VerschilTaxatieGuide").then((m) => ({ default: m.VerschilTaxatieGuide })),
  "taxatie-lager-dan-koopsom-wat-nu": () =>
    import("./guides/TaxatieLagerGuide").then((m) => ({ default: m.TaxatieLagerGuide })),
  "hoeveel-eigen-geld-bij-overbieden": () =>
    import("./guides/EigenGeldGuide").then((m) => ({ default: m.EigenGeldGuide })),
  "woz-vs-vraagprijs-bod-te-hoog": () =>
    import("./guides/WOZGuide").then((m) => ({ default: m.WOZGuide })),
  "overbieden-meefinancieren-hypotheek": () =>
    import("./guides/MeefinancierenGuide").then((m) => ({ default: m.MeefinancierenGuide })),
  "bod-uitbrengen-wat-staat-erin": () =>
    import("./guides/BodUitbrengenGuide").then((m) => ({ default: m.BodUitbrengenGuide })),
  "overbieden-amsterdam-q1-2026": () =>
    import("./guides/AmsterdamGuide").then((m) => ({ default: m.AmsterdamGuide })),
  "wel-of-niet-overbieden-beslisboom": () =>
    import("./guides/BeslisboomGuide").then((m) => ({ default: m.BeslisboomGuide })),
  "beste-buurten-amsterdam-2026": () =>
    import("./guides/BesteBuurtenAmsterdamGuide").then((m) => ({
      default: m.BesteBuurtenAmsterdamGuide,
    })),
  "best-neighbourhoods-amsterdam-2026": () =>
    import("./guides/BestNeighbourhoodsAmsterdamGuide").then((m) => ({
      default: m.BestNeighbourhoodsAmsterdamGuide,
    })),
  "beste-buurten-utrecht-2026": () =>
    import("./guides/BesteBuurtenUtrechtGuide").then((m) => ({
      default: m.BesteBuurtenUtrechtGuide,
    })),
  "best-neighbourhoods-utrecht-2026": () =>
    import("./guides/BestNeighbourhoodsUtrechtGuide").then((m) => ({
      default: m.BestNeighbourhoodsUtrechtGuide,
    })),
  "huis-kopen-stappenplan-2026": () =>
    import("./guides/HuisKopenStappenplanGuide").then((m) => ({
      default: m.HuisKopenStappenplanGuide,
    })),
  "buying-a-house-netherlands-2026": () =>
    import("./guides/BuyingAHouseNetherlandsGuide").then((m) => ({
      default: m.BuyingAHouseNetherlandsGuide,
    })),
  "maximale-hypotheek-2026": () =>
    import("./guides/MaximaleHypotheekGuide").then((m) => ({ default: m.MaximaleHypotheekGuide })),
  "maximum-mortgage-netherlands-2026": () =>
    import("./guides/MaximumMortgageNetherlandsGuide").then((m) => ({
      default: m.MaximumMortgageNetherlandsGuide,
    })),
  "energielabel-woning-2026": () =>
    import("./guides/EnergielabelGuide").then((m) => ({ default: m.EnergielabelGuide })),
  "energy-label-house-netherlands-2026": () =>
    import("./guides/EnergyLabelNetherlandsGuide").then((m) => ({
      default: m.EnergyLabelNetherlandsGuide,
    })),
  "kosten-koper-2026": () =>
    import("./guides/KostenKoperGuide").then((m) => ({ default: m.KostenKoperGuide })),
  "buyers-costs-netherlands-2026": () =>
    import("./guides/BuyersCostsNetherlandsGuide").then((m) => ({
      default: m.BuyersCostsNetherlandsGuide,
    })),
};

// Synchronous cache of resolved guide components. The build-time prerender fills
// it via preloadGuides() so the synchronous renderToString emits full article
// HTML; in the browser it fills on first navigation while the route's <Suspense>
// boundary (App.tsx) shows the fallback.
const resolved = new Map<string, ComponentType>();
const inflight = new Map<string, Promise<void>>();

function makeGuide(slug: string, load: Loader): ComponentType {
  return function Guide() {
    const Comp = resolved.get(slug);
    if (Comp) return <Comp />;
    let p = inflight.get(slug);
    if (!p) {
      p = load().then((m) => {
        resolved.set(slug, m.default);
      });
      inflight.set(slug, p);
    }
    // Suspend until the chunk resolves; App's <Suspense> shows the fallback.
    throw p;
  };
}

// slug → component for every article. Unknown slugs are absent, so BlogPost
// redirects them to the /blog index.
export const COMPONENTS: Record<string, ComponentType> = Object.fromEntries(
  Object.entries(loaders).map(([slug, load]): [string, ComponentType] => [
    slug,
    makeGuide(slug, load),
  ]),
);

// Resolve every guide module up front so the synchronous SSR prerender renders
// articles without suspending. Build-time only — never called in the browser.
export async function preloadGuides(): Promise<void> {
  await Promise.all(
    Object.entries(loaders).map(([slug, load]) =>
      load().then((m) => {
        resolved.set(slug, m.default);
      }),
    ),
  );
}

// Warm the chunk for the article at `pathname` (if any) and cache it. main.tsx
// awaits this before the first client render on a /blog/<slug> direct hit, so
// React re-renders the prerendered article in place instead of briefly swapping
// it for the Suspense fallback. Resolves immediately for every other route.
export function preloadGuideForPath(pathname: string): Promise<void> {
  const slug = pathname.match(/^\/blog\/([^/]+)\/?$/)?.[1];
  if (!slug) return Promise.resolve();
  const load = loaders[slug];
  if (!load) return Promise.resolve();
  return load().then((m) => {
    resolved.set(slug, m.default);
  });
}
